import React, { useEffect, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react';

import { getVariationContent } from '@rover/components/src/MarketingOptIn/constants';
import { Image, Text } from '@rover/kibble/core';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import MarketingOptInContent from './components/MarketingOptInContent';
import {
  OptInModalAccept,
  OptInModalDecline,
  OptInModalDismiss,
  OptInModalView,
} from './analyticsEvents';
import { MarketingOptInPromptVariation, MarketingOptInProps } from './types';
import { getVariationKey } from './utils';

const IMAGE_WIDTH = '192px';
const IMAGE_HEIGHT = '192px';

const MARKETING_OPT_IN_VERSION = MarketingOptInPromptVariation.CONTACTABILITY_M1;

const MarketingOptIn = ({
  petType,
  channels = [],
  marketingOptInSource = 'services',
  countryCode,
  isOpen: externalIsOpen,
  onRequestClose,
  onOptIn,
  onDecline,
  onDismiss,
}: MarketingOptInProps): JSX.Element => {
  const { i18n } = useLingui();
  const variationKey = getVariationKey(petType, channels);
  const {
    image,
    imageDescription,
    title,
    description,
    mainCTALabel,
    secondaryCTALabel,
    tertiaryCTALabel,
  } = getVariationContent(variationKey, i18n);

  const [isOpen, setIsOpen] = useState(true);

  const eventParams = useMemo(
    () => ({
      channels,
      marketingOptInSource,
      marketingOptInVersion: MARKETING_OPT_IN_VERSION,
      countryCode,
    }),
    [channels, marketingOptInSource, countryCode]
  );

  useEffect(() => {
    emitAnalyticsEvent(new OptInModalView(eventParams, eventParams));
  }, [eventParams]);

  const handleMainCtaPress = (): void => {
    emitAnalyticsEvent(new OptInModalAccept(eventParams, eventParams));
    onOptIn();
    setIsOpen(false);
  };

  const handleSecondaryCtaPress = (): void => {
    emitAnalyticsEvent(new OptInModalDecline(eventParams, eventParams));
    onDecline();
    setIsOpen(false);
  };

  const handleTertiaryCtaPress = (): void => {
    const dismissParams = { ...eventParams, action: 'click-maybe' as const };
    emitAnalyticsEvent(new OptInModalDismiss(dismissParams, dismissParams));
    onDismiss();
    setIsOpen(false);
  };

  const imageElement = (
    <Image src={image} description={imageDescription} width={IMAGE_WIDTH} height={IMAGE_HEIGHT} />
  );

  const bodyElement = <Text size="200">{description}</Text>;

  return (
    <MarketingOptInContent
      isOpen={externalIsOpen !== undefined ? externalIsOpen : isOpen}
      onRequestClose={() => {
        const dismissParams = { ...eventParams, action: 'close-sheet' as const };
        emitAnalyticsEvent(new OptInModalDismiss(dismissParams, dismissParams));
        onRequestClose();
      }}
      image={imageElement}
      title={title}
      body={bodyElement}
      mainCTALabel={mainCTALabel}
      secondaryCTALabel={secondaryCTALabel}
      tertiaryCTALabel={tertiaryCTALabel}
      onMainCtaPress={handleMainCtaPress}
      onSecondaryCtaPress={handleSecondaryCtaPress}
      onTertiaryCtaPress={handleTertiaryCtaPress}
    />
  );
};

export default MarketingOptIn;
